/**
 * THE FIELD SHAPES THE ENTITY PAGE BUILDS — used by the form and the entity chat (ADR 0009).
 * Pure: no Angular, no I/O, no clock (a timestamp is passed in), and nothing is mutated.
 * ⚠ `import type` only — see entity-form-rules.ts.
 */
import type { Entity, TypeData } from '../../../../model/entity.model';

/** What the Add Field form opens with. */
export function blankField(): TypeData {
  return {
    name: '', type: 'STRING', isUnique: false, primaryKey: false, startIncrementFrom: 0,
    referenceEntityName: '', referenceEntityColumnName: 'id', extendedAttributes: false,
    id: '', data: [], defaultValue: '', description: '',
  };
}

export interface TypeDefaults {
  /** The field with the new type's defaults applied. A new object. */
  field: TypeData;
  /** False for a reference until a target is picked — the form's Add button stays hidden. */
  addButtonEnabled: boolean;
}

/**
 * What choosing a type does to the field being edited: nested fields cleared, extended attributes off
 * unless it is a list of items, and the type's default seeded. Settings a type does not touch are kept
 * as they were — including a `defaultMode` left over from an earlier TimeStamp choice, as the form does.
 */
export function applyTypeDefaults(field: TypeData): TypeDefaults {
  const f: TypeData = { ...field, data: [] };
  if (f.type !== 'ARRAY_OF_OBJECT') f.extendedAttributes = false;
  let addButtonEnabled = true;
  switch (f.type) {
    case 'STRING':
      f.defaultValue = '';
      break;
    case 'NUMBER':
      f.defaultValue = 0;
      break;
    case 'BOOLEAN':
      f.defaultValue = false;
      break;
    case 'AUTO_INCREMENT':
      f.startIncrementFrom = 0;
      f.defaultValue = 0;
      break;
    case 'TIMESTAMP':
      // Was (until 2026-10-10): f.defaultMode = 'now'; // fresh per record
      // Why: owner rule (zavo_bug_oct_15) — an untouched date stays empty; 'now' only when the builder picks it.
      f.defaultMode = 'none';
      f.defaultValue = '';   // used only when the mode is 'fixed'
      break;
    case 'GEOMETRY':
      f.defaultValue = { type: 'Polygon', coordinates: [] };
      break;
    case 'UUID':
    case 'ATTACHMENT':
      f.primaryKey = false;
      f.defaultValue = '';
      break;
    case 'ENTITY_REFERENCE':
      addButtonEnabled = false;
      f.referenceEntityName = '';
      f.referenceEntityColumnName = 'id';
      break;
  }
  return { field: f, addButtonEnabled };
}

const systemColumn = (
  name: string, type: string, isUnique: boolean, primaryKey: boolean, referenceEntityName: string, defaultValue: unknown,
): TypeData => ({
  name, type, isUnique, primaryKey, startIncrementFrom: 0,
  referenceEntityName, referenceEntityColumnName: 'id', id: '', data: [], defaultValue,
});

/** The three fields every list of items starts with. `parent_id` points at its container (entity or list). */
export function aobSystemFields(parentRefId: string): TypeData[] {
  return [
    systemColumn('id', 'UUID', true, true, '', ''),
    systemColumn('id_auto_increment', 'AUTO_INCREMENT', true, false, '', 0),
    systemColumn('parent_id', 'ENTITY_REFERENCE', true, false, parentRefId, ''),
  ];
}

/** The six key/value columns a list with extended attributes carries. `nowIso` seeds `systemTimestamp`. */
export function extendedAttributeFields(nowIso: string): TypeData[] {
  return [
    systemColumn('systemKey', 'STRING', false, false, '', ''),
    systemColumn('systemType', 'STRING', false, false, '', ''),
    systemColumn('systemString', 'STRING', false, false, '', ''),
    systemColumn('systemNumber', 'NUMBER', false, false, '', 0),
    systemColumn('systemBoolean', 'BOOLEAN', false, false, '', false),
    systemColumn('systemTimestamp', 'TIMESTAMP', false, false, '', nowIso),
  ];
}

/**
 * A list of items' starting fields, exactly as Add Field builds them (plan 4c, spec Q41): the three system fields
 * (`parent_id` → the container), plus the six extended-attribute columns when asked. One builder for the form's
 * Add, the form's Edit (a field turned into a list while New) and the entity chat.
 */
export function listFields(parentRefId: string, extended: boolean, nowIso: string): TypeData[] {
  return extended ? [...aobSystemFields(parentRefId), ...extendedAttributeFields(nowIso)] : aobSystemFields(parentRefId);
}

/**
 * What Create Entity opens with: no name, no id, `id` and `id_auto_increment`. A database entity starts
 * New; a local one starts Local (scalar-ish, no table). Used by the form and by the entity chat.
 */
export function entitySkeleton(kind: 'database' | 'local'): Entity {
  return {
    name: '',
    type: 'TABLE',
    id: '',
    status: kind === 'local' ? 'LOCAL' : 'NEW',
    data: [
      systemColumn('id', 'UUID', true, true, '', ''),
      systemColumn('id_auto_increment', 'AUTO_INCREMENT', true, false, '', 0),
    ],
  };
}

/**
 * A new sample row as the form's "+ Add row" seeds it: each cell is the field's default, else a zero value.
 * BOOLEAN is `false` here, not `true` as in page.service: this seeds a SAMPLE ROW, not a column default.
 * ⚠ A field with "No default (null)" still seeds 0 / '' here, on purpose — a sample row is data a builder
 * is shown and edits, and a blank cell would look like a bug. Do not "unify" with defaultValueForField.
 */
export function sampleRowSeed(fields: readonly TypeData[]): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  for (const f of fields) {
    row[f.name] = f.defaultValue !== undefined && f.defaultValue !== ''
      ? f.defaultValue
      : f.type === 'NUMBER' ? 0
      : f.type === 'BOOLEAN' ? false
      : f.type === 'GEOMETRY' ? { type: 'Polygon', coordinates: [] }
      : '';
  }
  return row;
}
